import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './animate-ui/components/buttons/button';
import { Card, CardContent } from './ui/card';
import { LogOut, ChevronDown, KeyRound, Shield, User } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Separator } from './ui/separator';
import ApiKeysManager from './ApiKeysManager';
import { Badge } from './ui/badge';

export const UserProfile: React.FC = () => {
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isApiKeysOpen, setIsApiKeysOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const handleAdminView = () => {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'admin');
    navigate({ pathname: location.pathname, search: params.toString() });
    setIsDropdownOpen(false);
  };

  const handleProfileView = () => {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'profile');
    navigate({ pathname: location.pathname, search: params.toString() });
    setIsDropdownOpen(false);
  };

  const { t } = useTranslation();

  return (
    <div className="relative">
      <motion.button
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center space-x-2">
          <div className="hidden sm:block text-left">
            <p className="text-sm font-medium text-foreground">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate max-w-32">{user.email}</p>
          </div>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground transition-transform ${
            isDropdownOpen ? 'rotate-180' : ''
          }`} 
        />
      </motion.button>

      <AnimatePresence>
        {isDropdownOpen && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 z-[90]"
              onClick={() => setIsDropdownOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 top-full mt-2 z-[100]"
            >
              <Card className="w-64 shadow-lg border">
                <CardContent className="p-4">
                  <div className="flex items-center">
                    <div className="flex-1 mt-4">
                      <p className="font-medium text-foreground truncate">{user.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    </div>
                    {user?.is_admin ? (
                      <Badge variant="success" className="ml-2 self-end">{t('user.admin_badge', 'Admin')}</Badge>
                    ) : null}
                  </div>
                  <Separator className="my-2" />
                  <div>
                    {user?.is_admin ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start mb-2"
                        onClick={handleAdminView}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        {t('home.tab_admin', 'Admin')}
                      </Button>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start mb-2"
                      onClick={handleProfileView}
                    >
                      <User className="w-4 h-4 mr-2" />
                      {t('user.profile', 'Profile')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start mb-2"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setIsApiKeysOpen(true);
                      }}
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      {t('user.api_keys', 'API keys')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('user.sign_out', 'Sign out')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <ApiKeysManager open={isApiKeysOpen} onOpenChange={setIsApiKeysOpen} />
    </div>
  );
};

export default UserProfile;
